import React, { useCallback, useEffect, useMemo, useState } from "react";

import { FileText } from "lucide-react";

import EmailTemplatePreviewModal from "./EmailTemplatePreviewModal";
import DatePicker from "../../Components/DatePicker";
import CommonInput from "../../Components/Input";
import Pagination from "../../Components/Pagination";
import Select from "../../Components/Select";
import Table from "../../Components/Table";
import TruncatedTooltip from "../../Components/TruncatedTooltip";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  fetchEmailActivity,
  fetchEmailActivityTemplates,
  EmailActivityListItem,
} from "../../Store/Reducers/EmailActivitySlice";
import {
  ALL_EMAIL_TYPES,
  buildEmailTypeOptions,
  findEmailTemplateByType,
  formatEmailType,
} from "../../Utils/Constants/EmailTypeLabels";
import { formatDateTime } from "../../Utils/Helper/Helper";

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [20, 25, 50];

const getRecipientEmails = (
  primary: string,
  all?: string[] | null
): string[] => {
  return Array.from(new Set([primary, ...(all ?? [])].filter(Boolean)));
};

const EmailActivityContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    listLoading,
    items,
    emailTypes,
    total,
    totalPages,
    hasNext,
    hasPrevious,
    templates,
    templatesFetched,
  } = useAppSelector(state => state.emailActivity);

  const [recipientSearch, setRecipientSearch] = useState("");
  const [debouncedRecipient, setDebouncedRecipient] = useState("");
  const [selectedEmailType, setSelectedEmailType] = useState(ALL_EMAIL_TYPES);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [previewEmailType, setPreviewEmailType] = useState<string | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedRecipient(recipientSearch);
      setPage(1);
    }, 500);
    return () => window.clearTimeout(timer);
  }, [recipientSearch]);

  useEffect(() => {
    if (!templatesFetched) {
      dispatch(fetchEmailActivityTemplates() as any);
    }
  }, [dispatch, templatesFetched]);

  const buildListParams = useCallback(() => {
    const params: {
      page: number;
      page_size: number;
      recipient?: string;
      email_type?: string;
      from_date?: string;
      to_date?: string;
    } = {
      page,
      page_size: pageSize,
    };

    if (debouncedRecipient.trim()) {
      params.recipient = debouncedRecipient.trim();
    }
    if (selectedEmailType && selectedEmailType !== ALL_EMAIL_TYPES) {
      params.email_type = selectedEmailType;
    }
    if (fromDate.trim()) {
      params.from_date = fromDate.trim();
    }
    if (toDate.trim()) {
      params.to_date = toDate.trim();
    }

    return params;
  }, [page, pageSize, debouncedRecipient, selectedEmailType, fromDate, toDate]);

  useEffect(() => {
    dispatch(fetchEmailActivity(buildListParams()) as any);
  }, [dispatch, buildListParams]);

  const emailTypeOptions = useMemo(
    () => buildEmailTypeOptions(emailTypes),
    [emailTypes]
  );

  const selectedPreviewTemplate = useMemo(
    () => findEmailTemplateByType(templates, previewEmailType),
    [templates, previewEmailType]
  );

  const handleEmailTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSelectedEmailType(event.target.value || ALL_EMAIL_TYPES);
    setPage(1);
  };

  const handleFromDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFromDate(event.target.value);
    setPage(1);
  };

  const handleToDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setToDate(event.target.value);
    setPage(1);
  };

  const handlePageSizeChange = (nextPageSize: number) => {
    setPageSize(nextPageSize);
    setPage(1);
  };

  const tableColumns = useMemo(
    () => [
      {
        key: "created_at",
        title: "Sent",
        width: "11%",
        render: (value: string) => formatDateTime(value, "DMY_SHORT").date,
      },
      {
        key: "email_type",
        title: "Type",
        width: "20%",
        render: (value: string) => formatEmailType(value),
      },
      {
        key: "recipient_email",
        title: "Recipient",
        width: "24%",
        render: (value: string, record: EmailActivityListItem) => {
          const emails = getRecipientEmails(value, record.recipient_emails);
          const extraCount = emails.length > 1 ? emails.length - 1 : 0;
          const label =
            extraCount > 0 ? `${value} + ${extraCount} more` : value;
          const tooltip = extraCount > 0 ? emails.join(", ") : value;

          return <TruncatedTooltip tooltip={tooltip}>{label}</TruncatedTooltip>;
        },
      },
      {
        key: "subject",
        title: "Subject",
        width: "35%",
        render: (value: string) => (
          <span className="block truncate" title={value}>
            {value}
          </span>
        ),
      },
      {
        key: "template",
        title: "Template",
        width: "10%",
        textalign: "center" as const,
        render: (_: unknown, record: EmailActivityListItem) => (
          <button
            type="button"
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-purple-50 text-purple-600 ring-1 ring-purple-200 transition-colors hover:bg-purple-100 hover:text-purple-700"
            title="View template preview"
            onClick={() => setPreviewEmailType(record.email_type)}
          >
            <FileText size={16} />
          </button>
        ),
      },
    ],
    []
  );

  return (
    <div className="w-full bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
      <div className="mb-6 grid grid-cols-1 gap-4 pt-2 sm:grid-cols-2 xl:grid-cols-4">
        <CommonInput
          label="Recipient"
          name="recipient"
          value={recipientSearch}
          onChange={event => setRecipientSearch(event.target.value)}
          placeholder="Search To / CC / BCC"
        />
        <Select
          label="Email Type"
          name="email_type"
          value={selectedEmailType}
          onChange={handleEmailTypeChange}
          options={emailTypeOptions}
          isShowPlaceHolder={false}
        />
        <DatePicker
          label="From Date"
          name="from_date"
          value={fromDate}
          onChange={handleFromDateChange}
          max={toDate || undefined}
        />
        <DatePicker
          label="To Date"
          name="to_date"
          value={toDate}
          onChange={handleToDateChange}
          min={fromDate || undefined}
        />
      </div>

      <Table
        columns={tableColumns}
        data={items}
        loading={listLoading}
        rowKey={record => record.email_tracking_id}
        className="w-full"
      />

      {total > 0 && (
        <Pagination
          mode="dynamic"
          currentPage={page}
          totalPages={totalPages}
          totalItems={total}
          pageSize={pageSize}
          hasNext={hasNext}
          hasPrevious={hasPrevious}
          onPageChange={setPage}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          className="mt-4"
        />
      )}

      <EmailTemplatePreviewModal
        isOpen={!!previewEmailType}
        toggle={() => setPreviewEmailType(null)}
        template={selectedPreviewTemplate}
        emailTypeLabel={
          previewEmailType ? formatEmailType(previewEmailType) : undefined
        }
      />
    </div>
  );
};

export default EmailActivityContainer;
